"use client";

import { useContext } from "react";
import { XIcon } from "lucide-react";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { PictureAsset } from "@/components/common/picture-asset";

const KLUB_HREF = "https://remedium.md/klubmedyka";

const offers: {
  pre: string;
  label: string;
  href: string;
  post?: string;
}[] = [
  {
    pre: "Zleć księgowość ",
    label: "inFakt",
    href: "https://remedium.md/klubmedyka/uslugi/praktyka-lekarska/infakt",
  },
  {
    pre: "Zabezpiecz swoją karierę ",
    label: "ubezpieczając się",
    href: "https://remedium.md/klubmedyka/uslugi/ubezpieczenie",
  },
  {
    pre: "Bądź aktywny z pakietem ",
    label: "Medicover Sport",
    href: "https://remedium.md/klubmedyka/uslugi/sport-i-aktywnosc/medicover-sport",
  },
  {
    pre: "Zachowaj pełną prywatność z ",
    label: "Wirtualnym Gabinetem",
    href: "https://remedium.md/klubmedyka/subskrypcja/wirtualne-biuro",
  },
  {
    pre: "Kupuj ",
    label: "samochody",
    href: "https://remedium.md/klubmedyka/samochody",
    post: " w każdym budżecie",
  },
  {
    pre: "Kup catering dietetyczny taniej z ",
    label: "Maczfit",
    href: "https://remedium.md/klubmedyka/znizka/catering-dietetyczny-maczfit",
  },
];

export const HomeKlubMedykaBanner = ({
  closeBanner,
}: {
  closeBanner: () => void;
}) => {
  const { logView } = useContext(ResourceContext);

  const handleCtaClick = () => {
    logEvent("home_banner_button_click", logView, {
      banner_type: "klub_medyka",
      banner_button_type: "cta",
    });
  };

  const handleOfferClick = (label: string) => () => {
    logEvent("home_banner_button_click", logView, {
      banner_type: "klub_medyka",
      banner_button_type: label,
    });
  };

  const marqueeGroup = (hidden: boolean) => (
    <div
      aria-hidden={hidden}
      className="flex shrink-0 items-center text-sm leading-tight text-white lg:text-base"
    >
      {offers.map((offer) => (
        <span key={offer.href} className="flex items-center">
          <span>{offer.pre}</span>
          <a
            href={offer.href}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={hidden ? -1 : undefined}
            onClick={handleOfferClick(offer.label)}
            className="hover:text-club-lime ml-1 underline decoration-1 underline-offset-2 transition-colors"
          >
            {offer.label}
          </a>
          {offer.post && (
            <span className={"ml-1 inline-block"}>{offer.post}</span>
          )}
          <span className="mx-4 inline-block size-1 shrink-0 rounded-full bg-white/55" />
        </span>
      ))}
    </div>
  );

  const sides = (
    <>
      <div className="absolute inset-0 flex animate-[kmSideOut_7s_ease-in-out_infinite] items-center justify-center gap-3 whitespace-nowrap text-white motion-reduce:animate-none md:justify-start">
        <RemediumIcon className="h-7 w-7 shrink-0 lg:h-9 lg:w-9" />
        <p className="text-xl leading-tight tracking-tight lg:text-2xl xl:text-3xl">
          Jesteś na <b className="font-bold">Remedium</b>?
        </p>
      </div>
      <div className="absolute inset-0 flex animate-[kmSideIn_7s_ease-in-out_infinite] items-center justify-center gap-3 whitespace-nowrap text-white opacity-0 motion-reduce:opacity-100 md:justify-start">
        <KmIcon className="h-7 w-7 shrink-0 lg:h-9 lg:w-9" />
        <p className="text-xl leading-tight tracking-tight lg:text-2xl xl:text-3xl">
          Witaj w <b className="font-bold">Klubie.</b>
        </p>
      </div>
    </>
  );

  const cta = (
    <a
      href={KLUB_HREF}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleCtaClick}
      className="bg-club-lime inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-nowrap text-black shadow-[0_4px_14px_-4px_rgba(206,255,62,0.45)] transition-all hover:scale-105 hover:bg-[#d8ff5a] active:scale-98"
    >
      <span>Przejdź do Klubu Medyka</span>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="size-4 shrink-0"
      >
        <path d="M7 7h10v10" />
        <path d="M7 17 17 7" />
      </svg>
    </a>
  );

  return (
    <div className="relative my-10 w-full overflow-hidden rounded-3xl bg-[#f5f4ef] md:h-[200px]">
      <PictureAsset
        source={
          "https://cdn.remedium.md/image/common/2026/06/02/15/bg-photo-v7-jpg-6a1ed9655aa8b"
        }
        ext="jpg"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full scale-[1.02] animate-[kmBgDrift_24s_ease-in-out_infinite] object-cover object-[left_47%] motion-reduce:animate-none"
      />

      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.45)_0%,rgba(0,0,0,0.1)_28%,rgba(0,0,0,0.1)_72%,rgba(0,0,0,0.4)_100%),linear-gradient(180deg,rgba(0,0,0,0.05)_60%,rgba(0,0,0,0.5)_100%)]" />

      <div className="absolute top-2 right-2 z-30 cursor-pointer p-2">
        <XIcon size={20} className="text-white/70" onClick={closeBanner} />
      </div>

      {/* Desktop */}
      <div className="relative z-10 hidden h-[200px] md:block">
        <div className="absolute top-1/2 left-[6%] h-10 w-[40%] -translate-y-1/2">
          {sides}
        </div>
        <div className="absolute top-1/2 right-[6%] -translate-y-1/2">
          {cta}
        </div>
        <div className="km-marquee-pause absolute right-0 bottom-7 left-0 overflow-hidden [mask-image:linear-gradient(90deg,transparent_0,#000_4%,#000_96%,transparent_100%)]">
          <div className="km-marquee-track flex w-max animate-[kmMarquee_55s_linear_infinite] motion-reduce:animate-none">
            {marqueeGroup(false)}
            {marqueeGroup(true)}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-8 text-center md:hidden">
        <div className="relative h-9 w-full max-w-[260px]">{sides}</div>
        {cta}
        <div className="km-marquee-pause w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent_0,#000_8%,#000_92%,transparent_100%)]">
          <div className="km-marquee-track flex w-max animate-[kmMarquee_45s_linear_infinite] motion-reduce:animate-none">
            {marqueeGroup(false)}
            {marqueeGroup(true)}
          </div>
        </div>
      </div>
    </div>
  );
};

const RemediumIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 63 63"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      d="M63 26.5248C48.373 26.5248 36.4752 14.627 36.4752 0H26.5248C26.5248 14.627 14.627 26.5248 0 26.5248V36.4752C14.627 36.4752 26.5248 48.373 26.5248 63H36.4752C36.4752 48.373 48.373 36.4752 63 36.4752V26.5248ZM31.5 44.6487C28.3301 39.2186 23.7814 34.6699 18.3513 31.5C23.7814 28.3159 28.3159 23.7814 31.5 18.3513C34.6841 23.7814 39.2186 28.3159 44.6487 31.5C39.2186 34.6841 34.6841 39.2186 31.5 44.6487Z"
      fill="white"
    />
  </svg>
);

const KmIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 59.1914 59.1914"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      d="M51.793 0C55.8791 0.000179355 59.1914 3.3132 59.1914 7.39941V51.793C59.1912 55.879 55.879 59.1912 51.793 59.1914H7.39941C3.3132 59.1914 0.000177297 55.8791 0 51.793V7.39941C0 3.31309 3.31309 0 7.39941 0H51.793ZM15.4746 23.9727L15.5303 24.0654H15.4746V35.4414C23.0585 35.4414 29.2186 41.7487 29.2188 49.4805H40.5947C40.5947 40.9164 36.3773 33.3324 29.9404 28.7266V28.708L43.7021 19.5703L37.9863 9.72949L15.4746 23.9727Z"
      fill="#CEFF3E"
    />
  </svg>
);
